import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import {
  TestPhase,
  TestPhases,
  isTestPhase,
} from "../../../../models/audit/controlSession";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { CreatableCombobox } from "../../../../components/ui/creatable-combobox";
import { useOptroTestOptions } from "../../../../hooks/useOptroTestOptions";
import { OptroPhaseSelect } from "./OptroPhaseSelect";

interface CreateSessionDialogProps {
  controlId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionName: string;
  onSessionNameChange: (name: string) => void;
  testPhase: TestPhase;
  onTestPhaseChange: (testPhase: TestPhase) => void;
  fyCycle: string;
  onFyCycleChange: (fyCycle: string) => void;
  fyCycleOptions: string[];
  onCreateSession: (optroTestId: number | null) => void;
  loading: boolean;
  disabled?: boolean;
}

export const CreateSessionDialog = ({
  controlId,
  open,
  onOpenChange,
  sessionName,
  onSessionNameChange,
  testPhase,
  onTestPhaseChange,
  fyCycle,
  onFyCycleChange,
  fyCycleOptions,
  onCreateSession,
  loading,
  disabled = false,
}: CreateSessionDialogProps) => {
  const { data, failed } = useOptroTestOptions(controlId, open);
  const [selectedOptroTestId, setSelectedOptroTestId] = useState("");

  // Reset selection on every (re)open AND on control switch (the dialog
  // instance survives both) so a stale pick never lingers.
  useEffect(() => {
    if (open) {
      setSelectedOptroTestId("");
    }
  }, [open, controlId]);

  const isLoading = data === null && !failed;

  // Local narrows the linked type into the JSX without a cast. Only
  // `linked: false` falls through to the unlinked branch — a LINKED control
  // with zero Optro tests gets its own empty state instead.
  const linkedOptions = data?.linked === true ? data.options : null;
  const availableOptroOptions = linkedOptions
    ? linkedOptions.filter((option) => option.selectable)
    : [];
  const noOptroTestsInOptro =
    linkedOptions !== null && linkedOptions.length === 0;
  const noSelectableOptroOptions =
    linkedOptions !== null &&
    linkedOptions.length > 0 &&
    availableOptroOptions.length === 0;

  const createDisabled =
    disabled ||
    isLoading ||
    failed ||
    noOptroTestsInOptro ||
    noSelectableOptroOptions ||
    (linkedOptions !== null && selectedOptroTestId === "");

  // The blocking states are mutually exclusive; point the button at the one message that's showing.
  let createButtonDescribedBy: string | undefined;
  if (failed) createButtonDescribedBy = "optroTestOptionsError";
  else if (isLoading) createButtonDescribedBy = "optroTestOptionsLoading";
  else if (noOptroTestsInOptro) createButtonDescribedBy = "optroNoTestsInOptro";
  else if (noSelectableOptroOptions)
    createButtonDescribedBy = "optroNoSelectableOptions";

  const handleCreateSession = () => {
    // A linked control always has a selection here (`createDisabled` blocks an
    // empty one), so the id fully encodes the mode: a number means the new
    // test maps to an Optro test.
    const optroTestId =
      linkedOptions !== null ? Number(selectedOptroTestId) : null;
    onCreateSession(optroTestId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="gap-2"
          disabled={disabled}
        >
          <Plus className="h-3 w-3" />
          Add Test
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Test</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            {/* No retry affordance: the hook already refetches on every
                (re)open, so closing and reopening the dialog IS the retry. */}
            {failed && (
              <p
                id="optroTestOptionsError"
                role="alert"
                className="col-span-4 text-sm text-red-600"
              >
                Couldn't load this control's Optro test options. Close the
                dialog and try again.
              </p>
            )}

            {!failed && isLoading && (
              <p
                id="optroTestOptionsLoading"
                className="col-span-4 text-sm text-stone-500"
              >
                Loading test options…
              </p>
            )}

            {!failed &&
              !isLoading &&
              linkedOptions !== null &&
              !noOptroTestsInOptro && (
                <>
                  {noSelectableOptroOptions && (
                    <p
                      id="optroNoSelectableOptions"
                      className="col-span-4 text-sm text-stone-500"
                    >
                      Every Optro test on this control already has a Midship
                      test.
                    </p>
                  )}
                  <label htmlFor="optroPhase" className="text-right">
                    Test Phase
                  </label>
                  <div className="col-span-3">
                    <OptroPhaseSelect
                      id="optroPhase"
                      options={linkedOptions}
                      value={selectedOptroTestId}
                      onChange={setSelectedOptroTestId}
                    />
                  </div>
                </>
              )}

            {!failed && !isLoading && noOptroTestsInOptro && (
              <p
                id="optroNoTestsInOptro"
                className="col-span-4 text-sm text-stone-500"
              >
                This control has no tests in Optro yet.
              </p>
            )}

            {!failed && !isLoading && linkedOptions === null && (
              <>
                <label htmlFor="sessionName" className="text-right">
                  Name
                </label>
                <Input
                  id="sessionName"
                  value={sessionName}
                  onChange={(e) => onSessionNameChange(e.target.value)}
                  className="col-span-3"
                  placeholder="Enter test name"
                />
                <label htmlFor="testPhase" className="text-right">
                  Test Phase
                </label>
                <Select
                  value={testPhase}
                  onValueChange={(value) => {
                    if (isTestPhase(value)) {
                      onTestPhaseChange(value);
                    }
                  }}
                >
                  <SelectTrigger id="testPhase" className="col-span-3">
                    <SelectValue placeholder="Select test phase" />
                  </SelectTrigger>
                  <SelectContent>
                    {TestPhases.map((phase: (typeof TestPhases)[number]) => (
                      <SelectItem key={phase.value} value={phase.value}>
                        {phase.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            <label htmlFor="fyCycle" className="text-right">
              FY
            </label>
            <div className="col-span-3">
              <CreatableCombobox
                value={fyCycle}
                onChange={onFyCycleChange}
                options={fyCycleOptions}
                testId="create-session-fy-cycle-combobox"
                placeholder="Select or create FY cycle..."
                emptyText="No FY cycles yet. Type to create one."
                createText="Create"
                modal
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateSession}
            loading={loading}
            disabled={createDisabled}
            aria-describedby={createButtonDescribedBy}
          >
            Create Test
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
